// frontend/src/components/Navbar.jsx
// Navegação do painel: barra lateral (fixa em ecrãs largos, gaveta no
// telemóvel) com a igreja activa e as páginas por secção; barra de topo com o
// comando rápido e o menu de utilizador.
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";
import { getAppNavItemsForUser, getMainNavItemsForUser, navItemMatchesQuery } from "../nav/appNavItems";
import { roleLabelPt } from "../utils/roleLabels";
import {
  Search,
  X,
  Menu,
  ChevronDown,
  ChevronsLeft,
  ChevronsRight,
  LogOut,
  LogOutIcon,
  ShieldCheck,
  Repeat,
} from "lucide-react";
import ThemeToggle from "./ThemeToggle";
import useSidebarCollapsed from "../hooks/useSidebarCollapsed";

function cx(...arr) {
  return arr.filter(Boolean).join(" ");
}

const SECTION_ORDER = ["Geral", "Culto e Escala", "Pessoas e Estrutura", "Financeiro", "Administração", "Outros"];

function getNavSection(link) {
  switch (link.to) {
    case "/dashboard":
    case "/plano-anual":
    case "/whatsapp":
      return "Geral";
    case "/importar-ofertas":
    case "/reports":
      return "Financeiro";
    case "/services":
    case "/schedules":
    case "/relatorio-do-culto":
      return "Culto e Escala";
    case "/membros":
    case "/departamentos":
      return "Pessoas e Estrutura";
    case "/seguranca":
    case "/dados-da-igreja":
    case "/utilizadores":
    case "/perfis":
    case "/registo-de-atividade":
    case "/public-admin":
    case "/plataforma":
      return "Administração";
    default:
      return "Outros";
  }
}

function initialsOf(text, fallback = "?") {
  const words = String(text || "")
    .replace(/["“”]/g, "")
    .split(/\s+/)
    .filter(Boolean);
  if (!words.length) return fallback;
  const picked = words.length === 1 ? [words[0]] : [words[0], words[words.length - 1]];
  return picked.map((w) => w[0]).join("").toUpperCase();
}

/** Logótipo da igreja, ou as iniciais sobre a cor da igreja. */
function ChurchMark({ church, size = "h-10 w-10" }) {
  if (church?.logoUrl) {
    return <img src={church.logoUrl} alt="" className={cx(size, "shrink-0 rounded-xl bg-white object-contain ring-1 ring-slate-200 dark:ring-slate-700")} />;
  }
  const color = church?.primaryColor || "";
  return (
    <div
      className={cx(
        size,
        "shrink-0 rounded-xl flex items-center justify-center text-sm font-extrabold text-white shadow-sm",
        color ? "" : "bg-gradient-to-br from-indigo-600 to-sky-600"
      )}
      style={color ? { backgroundColor: color } : undefined}
      aria-hidden="true"
    >
      {initialsOf(church?.shortName || church?.name, "IG").slice(0, 2)}
    </div>
  );
}

/**
 * Conteúdo da barra lateral (ecrã largo e gaveta do telemóvel). Recolhida,
 * mostra só os ícones; o nome aparece ao passar o rato (title) e é lido pelos
 * leitores de ecrã (aria-label).
 */
function SidebarContent({ user, sections, onNavigate, collapsed = false, onToggleCollapse }) {
  const church = user?.church;
  const churchName = church?.shortName || church?.name || "Painel";
  return (
    <div className="flex h-full flex-col">
      <div className={cx("border-b border-slate-200/80 dark:border-slate-700/60", collapsed ? "px-2 py-4" : "px-4 pt-5 pb-4")}>
        <Link
          to="/dashboard"
          onClick={onNavigate}
          title={collapsed ? church?.name || churchName : undefined}
          aria-label={collapsed ? churchName : undefined}
          className={cx(
            "flex items-center gap-3 rounded-2xl focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-300",
            collapsed && "justify-center"
          )}
        >
          <ChurchMark church={church} />
          {collapsed ? null : (
            <div className="min-w-0">
              <div className="truncate text-sm font-extrabold text-slate-900 dark:text-slate-100" title={church?.name}>
                {churchName}
              </div>
              <div className="truncate text-xs text-slate-500 dark:text-slate-400">Painel da igreja</div>
            </div>
          )}
        </Link>
        {user?.isSuperAdmin ? (
          <Link
            to="/plataforma"
            onClick={onNavigate}
            title={collapsed ? "Trocar de igreja" : undefined}
            aria-label={collapsed ? "Trocar de igreja" : undefined}
            className={cx(
              "mt-3 flex items-center gap-1.5 rounded-lg py-1 text-xs font-semibold text-indigo-700 dark:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-950/40",
              collapsed ? "justify-center px-0" : "w-fit px-2"
            )}
          >
            <Repeat className="h-3.5 w-3.5" /> {collapsed ? null : "Trocar de igreja"}
          </Link>
        ) : null}
      </div>

      <nav className={cx("flex-1 overflow-y-auto py-4", collapsed ? "px-2" : "px-3")} aria-label="Páginas do painel">
        {sections.map(({ section, items }, idx) => (
          <div key={section} className="mb-4 last:mb-0">
            {collapsed ? (
              idx > 0 ? <div className="mx-2 mb-3 border-t border-slate-200/80 dark:border-slate-700/60" aria-hidden="true" /> : null
            ) : (
              <div className="px-3 pb-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500">{section}</div>
            )}
            <div className="grid gap-0.5">
              {items.map(({ to, label, end, Icon }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={!!end}
                  onClick={onNavigate}
                  title={collapsed ? label : undefined}
                  aria-label={collapsed ? label : undefined}
                  className={({ isActive }) =>
                    cx(
                      "flex items-center rounded-xl py-2 text-sm font-semibold transition",
                      collapsed ? "justify-center px-0" : "gap-2.5 px-3",
                      "focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-200 dark:focus-visible:ring-indigo-800/50",
                      isActive
                        ? "bg-indigo-50 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300"
                        : "text-slate-600 dark:text-slate-400 hover:bg-slate-100/80 dark:hover:bg-slate-800/60 hover:text-slate-900 dark:hover:text-slate-100"
                    )
                  }
                >
                  {Icon ? <Icon className={cx("shrink-0 opacity-80", collapsed ? "h-5 w-5" : "h-4 w-4")} /> : null}
                  {collapsed ? null : <span className="truncate">{label}</span>}
                </NavLink>
              ))}
            </div>
          </div>
        ))}
      </nav>

      {onToggleCollapse ? (
        <div className={cx("border-t border-slate-200/80 dark:border-slate-700/60 py-3", collapsed ? "px-2" : "px-3")}>
          <button
            type="button"
            onClick={onToggleCollapse}
            title={collapsed ? "Expandir a barra" : undefined}
            aria-label={collapsed ? "Expandir a barra lateral" : "Recolher a barra lateral"}
            aria-expanded={!collapsed}
            className={cx(
              "flex w-full items-center rounded-xl py-2 text-sm font-semibold text-slate-500 dark:text-slate-400 transition hover:bg-slate-100/80 dark:hover:bg-slate-800/60 hover:text-slate-900 dark:hover:text-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-200 dark:focus-visible:ring-indigo-800/50",
              collapsed ? "justify-center px-0" : "gap-2.5 px-3"
            )}
          >
            {collapsed ? <ChevronsRight className="h-5 w-5" /> : <ChevronsLeft className="h-4 w-4" />}
            {collapsed ? null : "Recolher"}
          </button>
        </div>
      ) : null}
    </div>
  );
}

export default function Navbar() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { user, logout, logoutAll } = useAuth();
  const [collapsed, toggleCollapsed] = useSidebarCollapsed();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);
  const userBtnRef = useRef(null);

  // Search / Command palette
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);
  const searchInputRef = useRef(null);
  const searchBtnRef = useRef(null);
  const searchPanelRef = useRef(null);

  const links = useMemo(
    () => getMainNavItemsForUser(user),
    [user]
  );
  const paletteItems = useMemo(
    () => getAppNavItemsForUser(user),
    [user]
  );

  const sections = useMemo(() => {
    const buckets = new Map();
    for (const link of links) {
      const section = getNavSection(link);
      if (!buckets.has(section)) buckets.set(section, []);
      buckets.get(section).push(link);
    }
    return SECTION_ORDER.filter((s) => buckets.has(s)).map((section) => ({ section, items: buckets.get(section) }));
  }, [links]);

  // Fecha menus ao mudar de rota
  useEffect(() => {
    setDrawerOpen(false);
    setUserMenuOpen(false);
    setSearchOpen(false);
    setSearchQ("");
    setActiveIdx(0);
  }, [pathname]);

  // ESC fecha menus
  useEffect(() => {
    function onKeyDown(e) {
      if (e.key !== "Escape") return;
      setDrawerOpen(false);
      setUserMenuOpen(false);
      setSearchOpen(false);
      setSearchQ("");
      setActiveIdx(0);
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  // Ctrl/Cmd + K abre pesquisa
  useEffect(() => {
    function onKeyDown(e) {
      const isK = e.key?.toLowerCase?.() === "k";
      const isMod = e.ctrlKey || e.metaKey;
      if (!isMod || !isK) return;
      e.preventDefault();
      setSearchOpen(true);
      setUserMenuOpen(false);
      setDrawerOpen(false);
      setTimeout(() => searchInputRef.current?.focus?.(), 0);
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  // Tranca scroll quando a gaveta ou a pesquisa estão abertas
  useEffect(() => {
    if (!drawerOpen && !searchOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev || "";
    };
  }, [drawerOpen, searchOpen]);

  // Fecha o menu de utilizador ao clicar fora
  useEffect(() => {
    function onPointerDown(e) {
      if (!userMenuOpen) return;
      if (userBtnRef.current?.contains(e.target)) return;
      if (userMenuRef.current?.contains(e.target)) return;
      setUserMenuOpen(false);
    }
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [userMenuOpen]);

  // Fecha a pesquisa ao clicar fora
  useEffect(() => {
    function onPointerDown(e) {
      if (!searchOpen) return;
      if (searchBtnRef.current?.contains(e.target)) return;
      if (searchPanelRef.current?.contains(e.target)) return;
      setSearchOpen(false);
      setSearchQ("");
      setActiveIdx(0);
    }
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [searchOpen]);

  async function handleLogoutClick() {
    setUserMenuOpen(false);
    try {
      const Swal = (await import("sweetalert2")).default;
      const result = await Swal.fire({
        title: "Sair da sessão?",
        text: "Você será desconectado da aplicação.",
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Sim, sair",
        cancelButtonText: "Cancelar",
        confirmButtonColor: "#2563eb",
        cancelButtonColor: "#64748b",
        reverseButtons: true,
      });
      if (result.isConfirmed) logout();
    } catch {
      if (window.confirm("Tem certeza que deseja sair?")) logout();
    }
  }

  async function handleLogoutAllClick() {
    setUserMenuOpen(false);
    try {
      const Swal = (await import("sweetalert2")).default;
      const result = await Swal.fire({
        title: "Encerrar em todos os dispositivos?",
        text: "Isso desconecta todas as sessões ativas.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Sim, encerrar tudo",
        cancelButtonText: "Cancelar",
        confirmButtonColor: "#ef4444",
        cancelButtonColor: "#64748b",
        reverseButtons: true,
      });
      if (result.isConfirmed) logoutAll();
    } catch {
      if (window.confirm("Encerrar sessões em todos os dispositivos?")) logoutAll();
    }
  }

  const filteredLinks = useMemo(() => {
    const q = searchQ.trim();
    if (!q) return paletteItems;
    return paletteItems.filter((l) => navItemMatchesQuery(l, q));
  }, [paletteItems, searchQ]);

  useEffect(() => {
    setActiveIdx(0);
  }, [searchQ, searchOpen, paletteItems.length]);

  const pickLink = useCallback(
    (l) => {
      if (!l?.to) return;
      setSearchOpen(false);
      setSearchQ("");
      setActiveIdx(0);
      navigate(l.to);
    },
    [navigate]
  );

  const onSearchKeyDown = useCallback(
    (e) => {
      if (!searchOpen) return;
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIdx((i) => Math.min(i + 1, Math.max(0, filteredLinks.length - 1)));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIdx((i) => Math.max(i - 1, 0));
      } else if (e.key === "Enter") {
        e.preventDefault();
        const chosen = filteredLinks[activeIdx];
        if (chosen) pickLink(chosen);
      }
    },
    [searchOpen, filteredLinks, activeIdx, pickLink]
  );

  const openSearch = () => {
    setSearchOpen(true);
    setUserMenuOpen(false);
    setDrawerOpen(false);
    setTimeout(() => searchInputRef.current?.focus?.(), 0);
  };

  if (pathname === "/login") return null;

  const userName = String(user?.name || "").trim() || user?.email || "Utilizador";
  const menuItemClass =
    "flex w-full items-center gap-2.5 rounded-xl px-3 py-2 text-left text-sm font-semibold text-slate-700 dark:text-slate-300 transition hover:bg-slate-100 dark:hover:bg-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-200 dark:focus-visible:ring-indigo-800/50";

  return (
    <>
      {/* Barra lateral fixa (ecrãs largos) */}
      <aside
        className={cx(
          "hidden lg:block fixed inset-y-0 left-0 z-40 border-r border-slate-200/80 dark:border-slate-700/60 bg-white dark:bg-slate-900 transition-[width] duration-200",
          collapsed ? "w-20" : "w-64"
        )}
      >
        <SidebarContent user={user} sections={sections} collapsed={collapsed} onToggleCollapse={toggleCollapsed} />
      </aside>

      {/* Gaveta (telemóvel / ecrãs estreitos) */}
      {drawerOpen && (
        <div className="lg:hidden fixed inset-0 z-50" role="dialog" aria-modal="true" aria-label="Menu de navegação">
          <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-[2px]" onClick={() => setDrawerOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-72 max-w-[85vw] bg-white dark:bg-slate-900 shadow-2xl">
            <button
              type="button"
              onClick={() => setDrawerOpen(false)}
              className="absolute right-3 top-4 z-10 inline-flex h-9 w-9 items-center justify-center rounded-xl text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
              aria-label="Fechar menu"
            >
              <X className="h-5 w-5" />
            </button>
            <SidebarContent user={user} sections={sections} onNavigate={() => setDrawerOpen(false)} />
          </div>
        </div>
      )}

      {/* Barra de topo */}
      <header className="sticky top-0 z-30 border-b border-slate-200/80 dark:border-slate-700/60 bg-white/85 dark:bg-slate-900/80 backdrop-blur-xl">
        <div className="flex h-16 items-center gap-3 px-4 sm:px-6">
          <button
            type="button"
            className="lg:hidden inline-flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-300 shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-200 dark:focus-visible:ring-indigo-800/50"
            onClick={() => setDrawerOpen(true)}
            aria-label="Abrir menu"
            aria-expanded={drawerOpen}
          >
            <Menu className="h-5 w-5" />
          </button>

          {/* Igreja activa no telemóvel (no ecrã largo está na barra lateral) */}
          <Link to="/dashboard" className="lg:hidden flex min-w-0 items-center gap-2">
            <ChurchMark church={user?.church} size="h-8 w-8" />
            <span className="truncate text-sm font-extrabold text-slate-900 dark:text-slate-100">
              {user?.church?.shortName || user?.church?.name || "Painel"}
            </span>
          </Link>

          <div className="flex-1" />

          <button
            ref={searchBtnRef}
            type="button"
            onClick={openSearch}
            className="inline-flex h-10 items-center gap-2.5 rounded-xl border border-slate-200 dark:border-slate-700/70 bg-white dark:bg-slate-900/70 px-3 text-sm font-semibold text-slate-600 dark:text-slate-300 transition hover:border-slate-300 dark:hover:border-slate-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-200 dark:focus-visible:ring-indigo-800/50"
            title="Comando rápido (Ctrl/⌘ + K)"
            aria-label="Comando rápido"
          >
            <Search className="h-4 w-4" />
            <span className="hidden md:inline">Comando rápido</span>
            <span className="hidden md:inline text-xs font-bold text-slate-400 dark:text-slate-500">Ctrl K</span>
          </button>

          {/* Menu de utilizador */}
          {user && (
            <div className="relative">
              <button
                ref={userBtnRef}
                type="button"
                onClick={() => setUserMenuOpen((v) => !v)}
                className="inline-flex h-10 items-center gap-2 rounded-xl border border-slate-200 dark:border-slate-700/70 bg-white dark:bg-slate-900/70 pl-1.5 pr-2 transition hover:border-slate-300 dark:hover:border-slate-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-200 dark:focus-visible:ring-indigo-800/50"
                aria-haspopup="menu"
                aria-expanded={userMenuOpen}
                aria-label="Menu da conta"
              >
                <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-slate-900 dark:bg-slate-100 text-xs font-bold text-white dark:text-slate-900">
                  {initialsOf(userName)}
                </span>
                <span className="hidden sm:flex max-w-[160px] flex-col text-left leading-tight">
                  <span className="truncate text-sm font-semibold text-slate-800 dark:text-slate-100">{userName}</span>
                  <span className="truncate text-[11px] text-slate-500 dark:text-slate-400">{user.roleName || roleLabelPt(user.role)}</span>
                </span>
                <ChevronDown className={cx("h-4 w-4 text-slate-500 transition", userMenuOpen && "rotate-180")} />
              </button>

              {userMenuOpen && (
                <div
                  ref={userMenuRef}
                  role="menu"
                  className="absolute right-0 top-12 z-50 w-72 rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-2 shadow-2xl ring-1 ring-black/5"
                >
                  <div className="px-3 py-2.5">
                    <div className="truncate text-sm font-bold text-slate-900 dark:text-slate-100">{userName}</div>
                    <div className="truncate text-xs text-slate-500 dark:text-slate-400">{user.email}</div>
                    <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-[11px] font-semibold">
                      <span className="rounded-full bg-slate-100 dark:bg-slate-800 px-2 py-0.5 text-slate-700 dark:text-slate-300">
                        {user.roleName || roleLabelPt(user.role)}
                      </span>
                      {user.isSuperAdmin ? (
                        <span className="rounded-full bg-indigo-50 dark:bg-indigo-950/50 px-2 py-0.5 text-indigo-700 dark:text-indigo-300">
                          Administrador da plataforma
                        </span>
                      ) : null}
                    </div>
                    <div className="mt-1.5 truncate text-xs text-slate-500 dark:text-slate-400">
                      {user.church?.name}
                    </div>
                  </div>
                  <div className="my-1 border-t border-slate-100 dark:border-slate-800" />
                  <Link to="/seguranca" role="menuitem" className={menuItemClass} onClick={() => setUserMenuOpen(false)}>
                    <ShieldCheck className="h-4 w-4" /> Segurança da conta
                  </Link>
                  <div className="px-1 py-1" role="none">
                    <ThemeToggle className="w-full justify-start border-transparent" labelClassName="inline" />
                  </div>
                  <div className="my-1 border-t border-slate-100 dark:border-slate-800" />
                  <button type="button" role="menuitem" className={menuItemClass} onClick={handleLogoutAllClick}>
                    <LogOutIcon className="h-4 w-4" /> Sair de todos os dispositivos
                  </button>
                  <button type="button" role="menuitem" className={menuItemClass} onClick={handleLogoutClick}>
                    <LogOut className="h-4 w-4" /> Sair
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </header>

      {/* Search / Command Palette */}
      {searchOpen && (
        <div className="fixed inset-0 z-[60]">
          <div className="absolute inset-0 bg-slate-900/30 backdrop-blur-[2px]" />
          <div
            ref={searchPanelRef}
            className="absolute left-1/2 top-16 -translate-x-1/2 w-[92vw] max-w-2xl
            rounded-3xl border border-white dark:border-slate-900/70 bg-white dark:bg-slate-900/90 backdrop-blur-xl shadow-2xl ring-1 ring-black/5"
            role="dialog"
            aria-label="Comando rápido"
          >
            <div className="p-4 sm:p-5 border-b border-white dark:border-slate-900/60 flex items-center gap-3">
              <div className="h-10 w-10 rounded-2xl bg-indigo-600/10 text-indigo-700 dark:text-indigo-300 flex items-center justify-center">
                <Search className="h-5 w-5" />
              </div>

              <div className="flex-1">
                <div className="text-xs font-semibold text-slate-500 dark:text-slate-400">Ir para…</div>
                <input
                  ref={searchInputRef}
                  value={searchQ}
                  onChange={(e) => setSearchQ(e.target.value)}
                  onKeyDown={onSearchKeyDown}
                  placeholder="Pesquisar por página ou palavra‑chave (ex.: financeiro, escala, membros)"
                  className="w-full mt-1 rounded-xl border border-slate-200 dark:border-slate-700/70 bg-white dark:bg-slate-900/70 px-3 py-2 text-slate-900 dark:text-slate-100
                  focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-200 dark:focus-visible:ring-indigo-800/50"
                  autoComplete="off"
                  spellCheck={false}
                />
              </div>

              <button
                type="button"
                onClick={() => {
                  setSearchOpen(false);
                  setSearchQ("");
                  setActiveIdx(0);
                  searchBtnRef.current?.focus?.();
                }}
                className="h-10 w-10 rounded-xl border border-slate-200 dark:border-slate-700/70 bg-white dark:bg-slate-900/70 text-slate-700 dark:text-slate-300
                hover:bg-white dark:hover:bg-slate-900 hover:border-slate-300 dark:hover:border-slate-600 transition
                focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-200 dark:focus-visible:ring-indigo-800/50 inline-flex items-center justify-center"
                aria-label="Fechar comando rápido"
                title="Fechar"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-2 sm:p-3 max-h-[55vh] overflow-auto">
              {filteredLinks.length ? (
                <div className="grid gap-1">
                  {filteredLinks.map((l, idx) => {
                    const active = idx === activeIdx;
                    const Icon = l.Icon;
                    return (
                      <button
                        key={l.to}
                        type="button"
                        onMouseEnter={() => setActiveIdx(idx)}
                        onClick={() => pickLink(l)}
                        className={cx(
                          "w-full text-left px-3 py-3 rounded-2xl border transition flex items-center gap-3",
                          active
                            ? "bg-indigo-50 dark:bg-indigo-950/40 border-indigo-100 dark:border-indigo-900/50 shadow-sm"
                            : "bg-white dark:bg-slate-900/60 border-white dark:border-slate-900/60 hover:bg-white dark:hover:bg-slate-900 hover:border-slate-200 dark:hover:border-slate-700/60"
                        )}
                      >
                        <div
                          className={cx(
                            "h-9 w-9 rounded-2xl flex items-center justify-center",
                            active ? "bg-indigo-600/10 text-indigo-700 dark:text-indigo-300" : "bg-slate-900/5 text-slate-700 dark:text-slate-300"
                          )}
                        >
                          {Icon ? <Icon className="h-5 w-5" /> : null}
                        </div>
                        <div className="flex-1">
                          <div className="font-semibold text-slate-900 dark:text-slate-100">{l.label}</div>
                          <div className="text-xs text-slate-500 dark:text-slate-400">{l.to}</div>
                        </div>
                        <div className="text-xs font-bold text-slate-400 dark:text-slate-500">ENTER</div>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="p-6 text-center text-slate-600 dark:text-slate-400">
                  Nenhum resultado para <span className="font-semibold text-slate-900 dark:text-slate-100">"{searchQ.trim()}"</span>.
                </div>
              )}
            </div>

            <div className="px-4 sm:px-5 py-3 border-t border-white dark:border-slate-900/60 flex flex-wrap gap-x-3 gap-y-1 items-center justify-between text-xs text-slate-500 dark:text-slate-400">
              <span>↑ ↓ navegar · Enter abrir · pesquisa ignora acentos</span>
              <span>Esc fechar</span>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
